import { faPencil, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import BreadcrumbItem from 'Components/Atoms/BreadcrumbItem/BreadcrumbItem';
import Button from 'Components/Atoms/Button/Button';
import Grid from 'Components/Atoms/Grid/Grid';
import NoValue from 'Components/Atoms/NoValue/NoValue';
import Spinner from 'Components/Atoms/Spinner/Spinner';
import Title from 'Components/Atoms/Title/Title';
import Breadcrumbs from 'Components/Molecules/Breadcrumbs/Breadcrumbs';
import ButtonGroup from 'Components/Molecules/ButtonGroup/ButtonGroup';
import EditButton from 'Components/Molecules/EditButton/EditButton';
import FormGroup from 'Components/Molecules/Form/FormGroup/FormGroup';
import Table from 'Components/Molecules/Table/Table';
import TableBody from 'Components/Molecules/TableBody/TableBody';
import TableCell from 'Components/Molecules/TableCell/TableCell';
import TableColumn from 'Components/Molecules/TableColumn/TableColumn';
import TableHeader from 'Components/Molecules/TableHeader/TableHeader';
import TableRow from 'Components/Molecules/TableRow/TableRow';
import Tabs from 'Components/Molecules/Tabs/Tabs';
import LayoutSidebar from 'Components/Organisms/Layout/LayoutSidebar/LayoutSidebar';
import LayoutCreateModal from 'Components/Organisms/LayoutCreateModal/LayoutCreateModal';
import LayoutFieldsModal from 'Components/Organisms/LayoutFieldsModal/LayoutFieldsModal';
import SettingsTemplate from 'Components/Templates/Page/Settings/SettingsTemplate';
import { useEffect, useMemo, useState } from 'react';
import 'react-dual-listbox/lib/react-dual-listbox.css';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { ROUTES } from 'Services/Route/Route.types';
import EntityHelper from 'Services/Settings/Entity/EntityHelper';
import { FieldType, getFieldTypeLabel } from 'Store/FieldManagement/PropTypes';
import { StoreState } from 'Store/Reducers';
import { fetchEntities } from 'Store/Settings/Entities/Actions';
import { getEntitiesById, isEntityLoading } from 'Store/Settings/Entities/Selectors';
import { fetchEntityTypesByEntityId } from 'Store/Settings/EntityTypes/Actions';
import { getEntityTypesByIdAsObject } from 'Store/Settings/EntityTypes/Selector';
import { fetchFields } from 'Store/Settings/Fields/Actions';
import { getFieldsById, isFieldsLoading } from 'Store/Settings/Fields/Selectors';
import { deleteLayout, fetchLayouts, patchLayout } from 'Store/Settings/Layout/Actions';
import { getLayoutsByEntityIdAsObject, getLayoutsByEntityTypeIdAsObject, getLayoutsById, isLayoutDeleting, isLayoutLoading, isLayoutPatching } from 'Store/Settings/Layout/Selectors';
import Field from '../../FieldsManagement/Fields/FieldTypes/Field';

const LayoutPage = ({
    isLoading, isPatching, isDeleting,
    entitiesById, entityTypesById,
    layoutsByEntityTypeId,
    layoutsAsObject, allLayouts, fieldsById,
    ...props
}: LayoutPageProps) => {
    const {
        entity_id,
        type_id
     } = useParams<{
        entity_id: string
        type_id: string
    }>();
    const [state, setState] = useState({
        isLoading: !layoutsAsObject?.[(entity_id || '')]?.[0] || !entitiesById?.[entity_id || '']?.id || Object.keys(fieldsById)?.length <= 0,
        isEdit: false,
        isDeleting: false,
        editFieldsModal: false,
        sidebarModal: false,
        createModal: false,
        active: ''
    });

    const [data, setData] = useState<any>({});

    useEffect(() => {
        props.fetchEntities();
        props.fetchLayouts();
        props.fetchFields();
        props.fetchEntityTypesByEntityId(entity_id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setState((state) => ({
            ...state,
            isLoading
        }));
    }, [isLoading]);

    useEffect(() => {
        if (!isPatching) {
            setState((state) => ({
                ...state,
                isEdit: false
            }));
        }
    }, [isPatching]);

    useEffect(() => {
        if (!isDeleting) {
            setState((state) => ({
                ...state,
                isDeleting: false
            }));
        }
    }, [isDeleting]);

    const entity = useMemo(() => {
        if (entity_id) {
            return {
                ...entitiesById?.[entity_id],
                formattedName: EntityHelper.getDisplayValueByName(entitiesById?.[entity_id]?.name)
            };
        }

        return {};
    }, [entitiesById, entity_id]);

    const entityType = useMemo(() => {
        if (!type_id) return {};

        return entityTypesById?.[type_id];
    }, [entityTypesById, type_id]);

    useEffect(() => {
        const layout = layoutsByEntityTypeId?.[(type_id || '')]?.[0];
        if (layout) {
            setState((state) => ({
                ...state,
                active: layout.id
            }));
        }
    }, [layoutsByEntityTypeId, type_id]);

    const layout = useMemo(() => {
        const item = layoutsByEntityTypeId?.[(type_id || '')];
        if (!item) return [];
        
        return item
            ?.sort((a, b) =>
                (a?.position < b?.position)
                ? -1
                : 1
            );
    }, [layoutsByEntityTypeId, type_id]);

    useEffect(() => {
        if (layout?.[0]) {
            setState((state) => ({
                ...state,
                active: layout?.[0].id
            }));
        }
    }, [layout]);

    useEffect(() => {
        setData(allLayouts?.[state.active] ?? {});
    }, [allLayouts, state.active]);

    const handleSaveGeneral = () => props.patchLayout(state.active, {
        name: data.name,
        attributes: data.attributes
    });

    return <SettingsTemplate
        breadcrumbs={
            <Breadcrumbs>
                <BreadcrumbItem path={ROUTES.SETTINGS.path}>Instellingen</BreadcrumbItem>
                <BreadcrumbItem path={ROUTES.SETTINGS.ENTITIES.getFullPath()}>Entiteiten</BreadcrumbItem>
                <BreadcrumbItem path={ROUTES.SETTINGS.ENTITIES.ENTITY.getFullPath(entity?.id)}>{entity?.formattedName || 'Oops...'}</BreadcrumbItem>
                <BreadcrumbItem path={ROUTES.SETTINGS.ENTITIES.ENTITY.LAYOUTS.getFullPath(entity?.id)}>Layout Management</BreadcrumbItem>
                <BreadcrumbItem>Type - { entityType?.name }</BreadcrumbItem>
            </Breadcrumbs>
        }
        description={`Instellingen voor de layout van ${(entity?.formattedName || 'oops...')}`}
    >
            { state.isLoading &&
                <Grid position='centered'>
                    <Spinner />
                    <Title size='small' weight='semibold'>Entiteiten laden...</Title>
                </Grid>
            }
            { !state.isLoading
            && !entityType?.id
            &&  <Grid position='centered'>
                    <Title size='small' weight='semibold'>Geen entiteit gevonden met de volgende id: {type_id}</Title>
                </Grid>
            }
            {/* { !state.isLoading
            && entityType?.id
            && layout.length === 0
            &&  <Grid>
                    <Button
                        size='large'
                        variant='border'
                        iconBefore={<FontAwesomeIcon icon={faPlus} />}
                        onClick={() => setState((state) => ({
                            ...state,
                            createModal: true
                        }))}
                    >
                        Toevoegen
                    </Button>
                </Grid>
            } */}
            { state.createModal && <LayoutCreateModal
                onClose={() => setState((state) => ({
                    ...state,
                    createModal: false
                }))}
                entity_id={entity_id}
                type_id={type_id}
            /> }
            { !state.isLoading
            && entityType?.id
            && <Grid axis='vertical'>
                    <Tabs
                        data={
                            [
                                ...layout,
                            ]?.map((i: any) => ({
                                id: i?.id,
                                title: i?.name
                            }))
                        }
                        actions={
                            <ButtonGroup>
                                <Button
                                    variant='tab'
                                    iconBefore={<FontAwesomeIcon icon={faPencil} />}
                                    onClick={() => setState({
                                        ...state,
                                        sidebarModal: true
                                    })}
                                >
                                    Sidebar aanpassen   
                                </Button>
                                <Button
                                    variant='tab'
                                    iconBefore={<FontAwesomeIcon icon={faPlus} />}
                                    onClick={() => setState({
                                        ...state,
                                        createModal: true
                                    })}
                                >
                                    Categorie toevoegen   
                                </Button>
                            </ButtonGroup>
                        }
                        active={state.active}
                        onChange={(id) => setState((state) => ({
                            ...state,
                            active: id,
                            isEdit: false
                        }))}
                        onDelete={(id: string) => props.deleteLayout(id)}
                        isDeleting={isDeleting}
                    />
                    <br />
                    <div>
                        <Grid axis='horizontal' position='between'>
                            <Title size='small' weight='semibold'>Algemene instellingen</Title>
                            <EditButton
                                variant='button'
                                isEdit={state.isEdit}
                                isLoading={isPatching}
                                onChange={(value) => setState({
                                    ...state,
                                    isEdit: value
                                })}
                                onSave={handleSaveGeneral}
                            />
                        </Grid>
                        <br />
                        <FormGroup title='Naam'>
                            <Field
                                isEdit={state.isEdit}
                                value={data?.name}
                                onChange={(value: any) => setData((data) => ({
                                    ...data,
                                    name: value
                                }))}
                                type={FieldType.Text}
                            />
                        </FormGroup>
                        <FormGroup title='Breedte'>
                            <Field
                                isEdit={state.isEdit}
                                value={data?.attributes?.width}
                                onChange={(value: any) => setData((data) => ({
                                    ...data,
                                    attributes: {
                                        ...data.attributes,
                                        width: value
                                    }
                                }))}
                                type={FieldType.Radio}
                                fieldProperties={{
                                    list: [{
                                        label: '50%',
                                        value: 1
                                    }, {
                                        label: '100%',
                                        value: 2
                                    }]
                                }}
                            />
                        </FormGroup>
                        <FormGroup title='As'>
                            <Field
                                isEdit={state.isEdit}
                                value={data?.attributes?.axis}
                                onChange={(value: any) => setData((data) => ({
                                    ...data,
                                    attributes: {
                                        ...data.attributes,
                                        axis: value
                                    }
                                }))}
                                type={FieldType.Radio}
                                fieldProperties={{
                                    list: [{
                                        label: 'Horizontaal',
                                        value: 1
                                    }, {
                                        label: 'Verticaal',
                                        value: 2
                                    }]
                                }}
                            />
                        </FormGroup>
                        <FormGroup title='Kolommen'>
                            <Field
                                isEdit={state.isEdit}
                                value={data?.attributes?.columns}
                                onChange={(value: any) => setData((data) => ({
                                    ...data,
                                    attributes: {
                                        ...data.attributes,
                                        columns: value
                                    }
                                }))}
                                type={FieldType.Radio}
                                fieldProperties={{
                                    list: [{
                                        label: '1 kolom',
                                        value: 1
                                    }, {
                                        label: '2 kolommen',
                                        value: 2
                                    }]
                                }}
                            />
                        </FormGroup>
                        <FormGroup title='Positie'>
                            { !state.isEdit && '#' }
                            <Field
                                isEdit={state.isEdit}
                                value={data?.attributes?.order}
                                onChange={(value: any) => setData((data) => ({
                                    ...data,
                                    attributes: {
                                        ...data.attributes,
                                        order: value
                                    }
                                }))}
                                type={FieldType.Number}
                            />
                        </FormGroup>
                    </div>
                    <br />
                    <Grid axis='horizontal' position='between'>
                        <Title size='small' weight='semibold'>Velden</Title>
                        <Button
                            size='small'
                            iconBefore={<FontAwesomeIcon icon={faPencil} />}
                            onClick={() => setState((state) => ({
                                ...state,
                                editFieldsModal: true
                            }))}
                        >
                            Aanpassen
                        </Button>
                    </Grid>
                    <Table>
                        <TableHeader>
                            <TableColumn>
                                Name
                            </TableColumn>
                            <TableColumn>
                                Type
                            </TableColumn>
                            <TableColumn>
                                Dataset
                            </TableColumn>
                        </TableHeader>
                        <TableBody>
                            {
                                data?.fields
                                ?.sort((a, b) => (fieldsById?.[a]?.name < fieldsById?.[b]?.name) ? -1 : 1)
                                ?.map(i => {
                                    const field = fieldsById?.[i];    
                                    
                                    return <TableRow>
                                        <TableCell
                                            primary
                                        >
                                            { field?.name || <NoValue /> }
                                        </TableCell>
                                        <TableCell>
                                            { getFieldTypeLabel(field?.type)  || <NoValue /> }
                                        </TableCell>
                                        <TableCell>
                                            <Field
                                                isEdit={false}
                                                value={field?.default?.dataset}
                                                type={FieldType.Multi_Selector}
                                                fieldProperties={{
                                                    ...field?.default,
                                                    alignLeft: true
                                                }}
                                            />
                                        </TableCell>
                                    </TableRow>;
                                })
                            }
                        </TableBody>
                    </Table>

                    { state.editFieldsModal && <LayoutFieldsModal
                        id={state.active}
                        entity_id={entity_id}
                        activeFields={data?.fields}
                        onClose={() => setState((state) => ({
                            ...state,
                            editFieldsModal: false
                        }))}    
                    /> }
                    {
                        state.sidebarModal &&
                        <LayoutSidebar
                            type_id={type_id}
                            entity_id={entity_id}
                            onClose={() => setState((state) => ({
                                ...state,
                                sidebarModal: false
                            }))}
                        />
                    }
                </Grid>
            }
    </SettingsTemplate>;
}

interface Props {

}

const mapState = (state: StoreState) => ({
    entitiesById: getEntitiesById(state),
    isLoading: isEntityLoading(state) || isLayoutLoading(state) || isFieldsLoading(state),
    isPatching: isLayoutPatching(state),
    isDeleting: isLayoutDeleting(state),
    layoutsAsObject: getLayoutsByEntityIdAsObject(state),
    allLayouts: getLayoutsById(state),
    fieldsById: getFieldsById(state),

    layoutsByEntityTypeId: getLayoutsByEntityTypeIdAsObject(state),
    entityTypesById: getEntityTypesByIdAsObject(state),
});

const mapDispatch = {
    fetchEntities,
    fetchLayouts,
    fetchFields,

    patchLayout,
    deleteLayout,

    fetchEntityTypesByEntityId
}

type LayoutPageProps = Props & ReturnType<typeof mapState> & typeof mapDispatch;

export default connect(mapState, mapDispatch)(LayoutPage);